import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import ReactFlow, { ReactFlowProvider, useNodesState, useEdgesState } from "reactflow";
import "reactflow/dist/style.css";
import axios from "axios";
import { ChevronRight, ArrowLeft, MessageSquare, Check, AlertCircle, Send, User } from "lucide-react";
import { CompanyNode, SubsidiaryNode, PersonNode, LogoNode, ServiceNode, JobNode, ScenarioNode, NoteNode, MetricNode } from "@/components/diagram/CustomNodes";
import AnimatedEdge from "@/components/diagram/AnimatedEdge";

import { API_URL } from "@/utils/apiConfig";
const API = API_URL;

const nodeTypes = { company: CompanyNode, subsidiary: SubsidiaryNode, person: PersonNode, logo: LogoNode, service: ServiceNode, job: JobNode, scenario: ScenarioNode, note: NoteNode, metric: MetricNode };
const edgeTypes = { animated: AnimatedEdge };

function ReviewInner() {
  const { reviewId } = useParams();
  const [reviewData, setReviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [slides, setSlides] = useState([]);
  const [slideIdx, setSlideIdx] = useState(0);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [commentStatus, setCommentStatus] = useState("comment");
  const [showPanel, setShowPanel] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/reviews/${reviewId}`);
        setReviewData(data);
        setComments(data.comments || []);
        if (data.nodes) setNodes(data.nodes);
        if (data.edges) setEdges(data.edges);

        // Build slides
        const s = [];
        s.push({ label: "Vue d'ensemble", nodeIds: (data.nodes || []).map(n => n.id), type: "main" });
        const targetIds = new Set((data.edges || []).map(e => e.target));
        const roots = (data.nodes || []).filter(n => !targetIds.has(n.id));
        const visited = new Set();
        const queue = roots.length > 0 ? [...roots] : data.nodes?.length > 0 ? [data.nodes[0]] : [];
        while (queue.length > 0) {
          const node = queue.shift();
          if (visited.has(node.id)) continue;
          visited.add(node.id);
          const childEdges = (data.edges || []).filter(e => e.source === node.id);
          const children = childEdges.map(e => (data.nodes || []).find(n => n.id === e.target)).filter(Boolean);
          s.push({ label: node.data?.title || "Etape", nodeIds: [node.id, ...children.map(c => c.id)], mainNodeId: node.id });
          children.forEach(c => { if (!visited.has(c.id)) queue.push(c); });
        }
        // Add pages as extra slides
        (data.pages || []).forEach(page => {
          s.push({ label: `Page: ${page.title}`, nodeIds: (page.nodes || []).map(n => n.id), type: "page", page });
        });
        setSlides(s);
      } catch (e) {
        setError("Ce lien de review n'est pas valide ou a expire.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [reviewId, setNodes, setEdges]);

  const goToSlide = useCallback((idx) => {
    if (idx < 0 || idx >= slides.length) return;
    setSlideIdx(idx);
    const slide = slides[idx];
    if (slide.type === "page" && slide.page) {
      setNodes(slide.page.nodes || []);
      setEdges(slide.page.edges || []);
    } else if (reviewData) {
      setNodes(reviewData.nodes || []);
      setEdges(reviewData.edges || []);
    }
  }, [slides, reviewData, setNodes, setEdges]);

  const submitComment = async () => {
    if (!newComment.trim()) return;
    try {
      const { data } = await axios.post(`${API}/reviews/comment`, {
        review_id: reviewId,
        slide_index: slideIdx,
        text: newComment,
        status: commentStatus,
        author_name: authorName || "Client",
      });
      setComments(prev => [...prev, data]);
      setNewComment("");
      setCommentStatus("comment");
    } catch {
      // ignore
    }
  };

  const slideComments = comments.filter(c => c.slide_index === slideIdx);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><p className="text-sm text-gray-400 animate-pulse">Chargement de la review...</p></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><p className="text-sm text-rose-500">{error}</p></div>;

  return (
    <div className="h-screen flex flex-col bg-white" data-testid="review-page">
      {/* Header */}
      <header className="h-12 bg-white border-b border-gray-200 flex items-center justify-between px-5 shrink-0">
        <div>
          <h1 className="font-bold text-sm text-gray-900">{reviewData?.title || "Review"}</h1>
          <span className="text-[10px] text-gray-400">Mode review client</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
            {slideIdx + 1} / {slides.length}
          </span>
          <button onClick={() => setShowPanel(v => !v)} className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${showPanel ? "bg-indigo-100 text-indigo-600" : "bg-gray-100 text-gray-400"}`}>
            <MessageSquare size={14} />
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Canvas */}
        <div className="flex-1 relative">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            proOptions={{ hideAttribution: true }}
          />

          {/* Slide label */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
            <span className="bg-white/90 backdrop-blur-sm border border-gray-200 shadow-sm px-4 py-1.5 rounded-full text-xs font-semibold text-gray-700">
              {slides[slideIdx]?.label || ""}
            </span>
          </div>

          {/* Navigation */}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
            <button onClick={() => goToSlide(slideIdx - 1)} disabled={slideIdx === 0} className="w-10 h-10 bg-white border border-gray-200 rounded-xl shadow-md flex items-center justify-center text-gray-500 disabled:opacity-30 hover:bg-gray-50">
              <ArrowLeft size={16} />
            </button>
            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-md">
              {slides.map((_, i) => (
                <button key={i} onClick={() => goToSlide(i)} className={`rounded-full transition-all ${i === slideIdx ? "w-6 h-2.5 bg-indigo-600" : "w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400"}`} />
              ))}
            </div>
            <button onClick={() => goToSlide(slideIdx + 1)} disabled={slideIdx >= slides.length - 1} className="w-10 h-10 bg-white border border-gray-200 rounded-xl shadow-md flex items-center justify-center text-gray-500 disabled:opacity-30 hover:bg-gray-50">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* Review panel */}
        {showPanel && (
          <div className="w-80 border-l border-gray-200 flex flex-col bg-gray-50/50 shrink-0" data-testid="review-panel">
            <div className="px-4 py-3 border-b border-gray-200 bg-white">
              <h3 className="text-xs font-bold text-gray-900 flex items-center gap-1.5">
                <MessageSquare size={12} /> Commentaires — Étape {slideIdx + 1}
              </h3>
            </div>

            {/* Comments list */}
            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
              {slideComments.length === 0 && (
                <p className="text-[10px] text-gray-400 text-center py-4">Aucun commentaire sur cette étape</p>
              )}
              {slideComments.map(c => (
                <div key={c.id} className={`rounded-lg p-3 text-xs ${
                  c.status === "approved" ? "bg-emerald-50 border border-emerald-200" :
                  c.status === "needs_change" ? "bg-amber-50 border border-amber-200" :
                  "bg-white border border-gray-200"
                }`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <User size={10} className="text-gray-400" />
                    <span className="font-semibold text-gray-700">{c.author_name}</span>
                    {c.status === "approved" && <Check size={10} className="text-emerald-600" />}
                    {c.status === "needs_change" && <AlertCircle size={10} className="text-amber-600" />}
                  </div>
                  <p className="text-gray-600 leading-relaxed">{c.text}</p>
                </div>
              ))}
            </div>

            {/* Input */}
            <div className="border-t border-gray-200 px-3 py-3 bg-white space-y-2">
              {!authorName && (
                <input
                  value={authorName}
                  onChange={e => setAuthorName(e.target.value)}
                  placeholder="Votre nom"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-xs focus:outline-none focus:border-indigo-400"
                  data-testid="review-author-input"
                />
              )}
              <div className="flex gap-1">
                {[
                  { key: "comment", label: "Commentaire", icon: MessageSquare, color: "gray" },
                  { key: "approved", label: "Approuvé", icon: Check, color: "emerald" },
                  { key: "needs_change", label: "À modifier", icon: AlertCircle, color: "amber" },
                ].map(opt => {
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.key}
                      onClick={() => setCommentStatus(opt.key)}
                      className={`flex-1 py-1.5 rounded-lg text-[10px] font-semibold flex items-center justify-center gap-1 transition-colors ${
                        commentStatus === opt.key
                          ? opt.color === "emerald" ? "bg-emerald-100 text-emerald-700 border border-emerald-300"
                            : opt.color === "amber" ? "bg-amber-100 text-amber-700 border border-amber-300"
                            : "bg-gray-100 text-gray-700 border border-gray-300"
                          : "bg-gray-50 text-gray-400 border border-gray-200"
                      }`}
                      data-testid={`review-status-${opt.key}`}
                    >
                      <Icon size={10} /> {opt.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-1.5">
                <textarea
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitComment(); } }}
                  placeholder="Votre commentaire..."
                  className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-xs focus:outline-none focus:border-indigo-400 resize-none min-h-[36px]"
                  rows={1}
                  data-testid="review-comment-input"
                />
                <button
                  onClick={submitComment}
                  disabled={!newComment.trim()}
                  className="w-9 h-9 bg-indigo-600 text-white rounded-lg flex items-center justify-center hover:bg-indigo-700 disabled:opacity-30 shrink-0"
                  data-testid="review-send-btn"
                >
                  <Send size={14} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ClientReviewPage() {
  return (
    <ReactFlowProvider>
      <ReviewInner />
    </ReactFlowProvider>
  );
}
